import { ChangeDetectionStrategy, Component, OnInit, inject, output, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { TranslateModule } from '@ngx-translate/core';
import type Keycloak from 'keycloak-js';
import { environment } from 'src/environments/environment';
import { AuthService, SSO_LOGOUT_KEY } from 'src/app/SayTS/auth.service';
import { SharedService } from 'src/app/_shared/shared.service';

/**
 * Hybridní SSO tlačítko — sdílí ho produkční i test login (test build login
 * komponentu nahrazuje fileReplacementem). Bez Keycloak konfigurace (nebo
 * s nedostupným Keycloakem) se nezobrazí. Při návratu z Keycloaku (code v URL)
 * init vrátí true a token se vymění za EML JWT.
 */
@Component({
  selector: 'app-sso-login',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [TranslateModule],
  template: `
    @if (ready()) {
      <div class="d-flex justify-content-center mt-3 pt-3 border-top">
        <button class="btn btn-outline-dark px-4" type="button" (click)="login()">
          {{'login.sign_in_sso'|translate}}
        </button>
      </div>
    }
  `,
})
export class SsoLoginComponent implements OnInit {
  private http = inject(HttpClient);
  private auth = inject(AuthService);
  private shared = inject(SharedService);

  readonly loggedIn = output<void>();
  readonly ready = signal(false);
  private kc: Keycloak | null = null;

  async ngOnInit() {
    if (this.shared.isLoggedIn()) return;
    try {
      const res: any = await this.http.get(`${environment.apiUrl}ssoConfig`, { params: { hideLoader: true } }).toPromise();
      if (!res?.keycloak) return;
      const { default: KeycloakCtor } = await import('keycloak-js');
      this.kc = new KeycloakCtor(res.keycloak);
      const authenticated = await this.kc.init({ pkceMethod: 'S256', checkLoginIframe: false });
      this.ready.set(true);
      if (!authenticated) return;

      await this.auth.loginUser({}, false, this.kc.token);
      if (this.shared.isLoggedIn()) {
        this.shared.storageSet(SSO_LOGOUT_KEY, this.kc.createLogoutUrl({ redirectUri: this.returnUrl() }));
        this.loggedIn.emit();
      }
    } catch (e) {
      console.warn("SSO unavailable", e);
    }
  }

  login() {
    void this.kc?.login({ redirectUri: this.returnUrl() });
  }

  private returnUrl() {
    return location.origin + location.pathname;
  }
}
